import { css } from '@emotion/react';
import {
  Input,
  Paragraph,
  Radio,
  Heading,
  Checkbox,
  ActionChip,
  ErrorMessage,
  tokens,
  BREAKPOINTS,
} from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { Gender } from 'shared';

import { FieldsetGroup } from '../atoms/FieldsetGroup';

const styles = {
  genderGroup: css`
    grid-column: 1;
    display: flex;
    gap: ${tokens.dimension['space-2']};
  `,
  phone: css`
    grid-column: 1;
  `,
  joinEurobonus: css`
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-4']};
  `,
  joinEurobonusText: css`
    width: 100%;

    @container main-container (min-width: ${BREAKPOINTS.layout.sm}px) {
      width: 75%;
    }
  `,
  bonusProgram: css`
    grid-column: 1 / -1;

    & input[type='checkbox'] {
      flex-shrink: 0;
    }
  `,
  bonusFormGroup: css`
    margin-top: ${tokens.dimension['space-5']};
    display: block;
  `,
  bonusAction: css`
    width: fit-content;
  `,
};

const genderOptions = [
  { label: 'common:male', value: Gender.MALE },
  { label: 'common:female', value: Gender.FEMALE },
];

type FieldInput<V, T extends object = object> = {
  id: string;
  value: V;
  onChange: (value: NonNullable<V>) => void;
  error?: string;
} & T;

export const FirstNameInput = ({
  id,
  value,
  onChange,
  onValidate,
  error,
}: FieldInput<
  string,
  {
    onValidate: () => void;
  }
>) => (
  <div>
    <Input
      id={id}
      data-testid="traveler-first-name"
      label={t('common:firstName')}
      value={value}
      onChange={e => {
        onChange(getCleanName(e.target.value));
      }}
      onBlur={onValidate}
      errorMessage={error}
      aria-describedby="first-name-hint"
      autoComplete="given-name"
      name={'firstName'}
    />
    <Paragraph id="first-name-hint" size="paragraph-2" color="secondary">
      {t('common:stateNameAsPassport', {
        defaultValue: 'State the name as in your passport. (Incl. middle names)',
      })}
    </Paragraph>
  </div>
);

export const LastNameInput = ({
  id,
  value,
  onChange,
  onValidate,
  error,
}: FieldInput<
  string,
  {
    onValidate: () => void;
  }
>) => (
  <Input
    id={id}
    data-testid="traveler-last-name"
    label={t('common:lastName')}
    value={value}
    onChange={e => {
      onChange(getCleanName(e.target.value));
    }}
    onBlur={onValidate}
    errorMessage={error}
    autoComplete="family-name"
    name={'lastName'}
  />
);

export const DateOfBirthInput = ({
  id,
  value,
  onChange,
  onValidate,
  error,
}: FieldInput<
  string | undefined,
  {
    onValidate: () => void;
  }
>) => (
  <Input
    id={id}
    data-testid="traveler-date-of-birth"
    label={`${t('common:dateOfBirth')} (yyyy-mm-dd)`}
    type="date-text"
    value={value ? new Date(value) : null}
    onChange={date => {
      onChange(date.toISOString().split('T')[0]);
    }}
    onBlur={onValidate}
    errorMessage={error}
    autoComplete="date-of-birth"
    name={'dateOfBirth'}
  />
);

export const GenderInput = ({ id, value, onChange, error }: FieldInput<string>) => (
  <FieldsetGroup css={styles.genderGroup} srLegend={`${t('common:gender')}`}>
    {genderOptions.map((option, index) => (
      <Radio
        id={index === 0 ? id : undefined}
        data-testid={`traveler-gender-${option.value}`}
        key={option.value}
        name={`${id}:gender`}
        label={t(option.label)}
        checked={value === option.value}
        onChange={() => {
          onChange(option.value);
        }}
        aria-describedby="gender-error"
        aria-invalid={Boolean(error)}
      />
    ))}
    {error && (
      <ErrorMessage id="gender-error" withIcon>
        {error}
      </ErrorMessage>
    )}
  </FieldsetGroup>
);

export const PhoneInput = ({ id, value, onChange, error }: FieldInput<string>) => (
  <div css={styles.phone}>
    <Input
      id={id}
      type="tel"
      data-testid="traveler-phone-number"
      value={value}
      label={t('common:phone', { defaultValue: 'Phone' })}
      name={'phoneNumber'}
      onChange={onChange}
      errorMessage={error}
    />
  </div>
);

export const EmailInput = ({
  id,
  value,
  onChange,
  onValidate,
  error,
}: FieldInput<
  string,
  {
    onValidate: () => void;
  }
>) => (
  <Input
    id={id}
    data-testid="traveler-email"
    label={t('common:email')}
    type="email"
    name={'email'}
    value={value}
    onBlur={onValidate}
    onChange={e => {
      onChange(e.target.value);
    }}
    errorMessage={error}
    autoComplete="email"
  />
);

export const JoinEurobonusInput = ({ value, onChange }: { value: boolean; onChange: (value: boolean) => void }) => (
  <FieldsetGroup ariaLabelledBy="join-eurobonus-heading" css={styles.joinEurobonus}>
    <Heading as="h3" size="title-2" id="join-eurobonus-heading">
      {t('travelers.joinEurobonusHeading')}
    </Heading>
    <Paragraph color="primary" size="paragraph-2" css={styles.joinEurobonusText}>
      {t('travelers.joinEurobonusText')}
    </Paragraph>
    <Checkbox
      label={t('travelers.joinEurobonusCheckboxLabel')}
      onChange={e => {
        onChange(e.target.checked);
      }}
      checked={value}
      data-testid="booking-join-eurobonus-checkbox"
    />
  </FieldsetGroup>
);

export const FrequentFlyerToggle = ({
  value,
  onChange,
  children,
}: FieldInput<
  boolean,
  {
    children?: React.ReactNode;
  }
>) => (
  <FieldsetGroup ariaLabelledBy="todo-add-hidden-legend" css={styles.bonusFormGroup}>
    {value ? (
      <>
        <ActionChip
          data-testid="traveler-remove-bonus-program"
          size="small"
          icon="Minus"
          border={false}
          css={styles.bonusAction}
          label={t('common:remove')}
          onClick={() => {
            onChange(false);
          }}
        />
        {children}
      </>
    ) : (
      <ActionChip
        data-testid="traveler-add-bonus-program"
        size="small"
        icon="PlusCircle"
        border={false}
        css={styles.bonusAction}
        label={t('travelers.addBonusProgram')}
        onClick={() => onChange(true)}
      />
    )}
  </FieldsetGroup>
);

const getCleanName = (name: string) => name.replace(/[0-9]/g, '');
